import React from 'react';
import { Box, Container, Button, useTheme, useMediaQuery } from '@mui/material';
import { useWebsiteSettings } from '../../contexts/WebsiteSettingsContext';
import { useLocation } from 'react-router-dom';
import { useAppMode } from '../../contexts/AppModeContext';
import MegaMenu from './MegaMenu';
import { jobMegaMenuData } from './jobMegaMenuData';

export const JOB_SECONDARY_LINKS: Array<{ label: string; path: string }> = [
  { label: 'IT & Telekommunikation', path: '/jobs?industry=IT%20%26%20Telekommunikation' },
  { label: 'Ingenieurwesen & Technik', path: '/jobs?industry=Ingenieurwesen%20%26%20Technik' },
  { label: 'Finanzen & Versicherung', path: '/jobs?industry=Finanzen%20%26%20Versicherung' },
  { label: 'Bildung & Erziehung', path: '/jobs?industry=Bildung%20%26%20Erziehung' },
  { label: 'Sozialer Sektor & Pflege', path: '/jobs?industry=Sozialer%20Sektor%20%26%20Pflege' },
  { label: 'Büroarbeit & Verwaltung', path: '/jobs?industry=B%C3%BCroarbeit%20%26%20Verwaltung' },
  { label: 'Vertrieb, Einkauf & Verkauf', path: '/jobs?industry=Vertrieb%2C%20Einkauf%20%26%20Verkauf' },
  { label: 'Kundenservice & Call Center', path: '/jobs?industry=Kundenservice%20%26%20Call%20Center' },
  { label: 'Gastronomie & Tourismus', path: '/jobs?industry=Gastronomie%20%26%20Tourismus' },
  { label: 'Bau, Handwerk & Produktion', path: '/jobs?industry=Bau%2C%20Handwerk%20%26%20Produktion' },
  { label: 'Transport, Logistik & Verkehr', path: '/jobs?industry=Transport%2C%20Logistik%20%26%20Verkehr' },
  { label: 'Immobilien', path: '/jobs?industry=Immobilien' },
  { label: 'Architektur & Bau', path: '/jobs?industry=Architektur%20%26%20Bau' },
  { label: 'Chemie & Pharma', path: '/jobs?industry=Chemie%20%26%20Pharma' },
  { label: 'Energie & Umwelt', path: '/jobs?industry=Energie%20%26%20Umwelt' },
  { label: 'Öffentlicher Dienst & Non-Profit', path: '/jobs?industry=%C3%96ffentlicher%20Dienst%20%26%20Non-Profit' },
  { label: 'Medien, PR & Kommunikation', path: '/jobs?industry=Medien%2C%20PR%20%26%20Kommunikation' },
  { label: 'Kunst & Kultur', path: '/jobs?industry=Kunst%20%26%20Kultur' },
  { label: 'Landwirtschaft & Tierhaltung', path: '/jobs?industry=Landwirtschaft%20%26%20Tierhaltung' },
  { label: 'Mini- & Nebenjobs', path: '/jobs?industry=Mini-%20%26%20Nebenjobs' },
  { label: 'Praktika', path: '/jobs?industry=Praktika' },
  { label: 'Ausbildung', path: '/jobs?industry=Ausbildung' },
  { label: 'Weitere Jobs', path: '/jobs?industry=Weitere%20Jobs' },
];

const JobSecondaryNavBar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTouch = useMediaQuery('(hover: none) and (pointer: coarse)');
  const location = useLocation();
  const { is_job } = useAppMode();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [hoveredCategory, setHoveredCategory] = React.useState<string | null>(null);
  const closeTimer = React.useRef<number | null>(null);
  const openTimer = React.useRef<number | null>(null);
  const [hidden, setHidden] = React.useState(false);
  const barRef = React.useRef<HTMLDivElement | null>(null);
  const [anchorTop, setAnchorTop] = React.useState<number>(0);
  const lastY = React.useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);
  const lastDir = React.useRef<'up' | 'down' | null>(null);
  const [categoriesEnabled, setCategoriesEnabled] = React.useState<Record<string, boolean>>({});
  const { settings: siteSettings } = useWebsiteSettings();
  const layout = (siteSettings?.layout || {}) as any;
  const maxWidth = typeof layout.container === 'string' && layout.container !== 'false' ? (layout.container as any) : false;
  const px = typeof layout.contentPaddingX === 'number' ? layout.contentPaddingX : 0;

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const clearOpenTimer = () => {
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };

  React.useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;
      const direction: 'up' | 'down' = delta > 2 ? 'down' : delta < -2 ? 'up' : (lastDir.current || 'down');
      if (direction !== lastDir.current) {
        lastDir.current = direction;
        setHidden(direction === 'down' && y > 10);
      } else if (y < 10) {
        setHidden(false);
      }
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    const compute = () => {
      const headerHeight = isMobile ? 44 : 52;
      const headerBorder = 1;
      if (hidden) {
        setAnchorTop(headerHeight + headerBorder);
        return;
      }
      const el = barRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        setAnchorTop(Math.max(headerHeight, Math.floor(rect.bottom)));
      } else {
        setAnchorTop(headerHeight + 40);
      }
    };
    compute();
    const ro = new ResizeObserver(() => compute());
    if (barRef.current) ro.observe(barRef.current);
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', compute as any);
      window.removeEventListener('resize', compute);
    };
  }, [hidden, isMobile, menuOpen]);

  React.useEffect(() => {
    if (hidden) setMenuOpen(false);
  }, [hidden]);

  React.useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch('/website-settings.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const existing: Record<string, boolean> = data?.categoriesEnabled || {};
        setCategoriesEnabled(existing);
      } catch {}
    };
    loadSettings();
  }, []);

  // Only show in job mode on desktop/tablet non-touch
  if (!is_job) return null;
  if (isMobile || isTouch) return null;

  const links = JOB_SECONDARY_LINKS;

  return (
    <Box
      ref={barRef}
      sx={{
        position: 'fixed',
        top: { xs: 44, md: 52 },
        left: 0,
        right: 'auto',
        width: 'calc(100% - var(--right-panel-offset, 0px))',
        height: 40,
        bgcolor: 'background.paper',
        borderBottom: menuOpen ? 'none' : '1px solid',
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.appBar - 1,
        display: 'flex',
        alignItems: 'center',
        transform: hidden ? 'translateY(-100%)' : 'translateY(0)',
        transition: 'transform 180ms ease',
        boxShadow: hidden || menuOpen ? 'none' : '0 2px 6px rgba(0,0,0,0.06)',
      }}
      onMouseLeave={() => {
        if (isMobile) return;
        clearOpenTimer();
        clearCloseTimer();
        closeTimer.current = window.setTimeout(() => setMenuOpen(false), 160) as unknown as number;
      }}
      onMouseEnter={() => {
        if (isMobile) return;
        clearCloseTimer();
      }}
    >
      <Container maxWidth={maxWidth} sx={{ px }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            '::-webkit-scrollbar': { height: 6 },
            '::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 999 },
          }}
        >
          {links
            .filter((link) => {
              if (Object.prototype.hasOwnProperty.call(categoriesEnabled, link.label)) {
                return !!categoriesEnabled[link.label];
              }
              return true;
            })
            .map((link) => {
              const isActive = location.pathname + location.search === link.path;
              return (
                <Button
                  key={link.label}
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() => {
                    clearOpenTimer();
                    clearCloseTimer();
                    setHoveredCategory(link.label);
                    setMenuOpen(true);
                  }}
                  onMouseEnter={() => {
                    if (isMobile) return;
                    clearCloseTimer();
                    setHoveredCategory(link.label);
                    if (!menuOpen) {
                      clearOpenTimer();
                      openTimer.current = window.setTimeout(() => setMenuOpen(true), 1000) as unknown as number;
                    } else {
                      setMenuOpen(true);
                    }
                  }}
                  onMouseLeave={() => {
                    if (isMobile) return;
                    clearOpenTimer();
                  }}
                  sx={{
                    textTransform: 'none',
                    color: isActive ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: { xs: '0.85rem', md: '0.95rem' },
                    px: 1.25,
                    minWidth: 'auto',
                    '&:hover': { color: 'primary.main', background: 'transparent' },
                  }}
                >
                  {link.label}
                </Button>
              );
            })}
        </Box>
      </Container>
      <MegaMenu
        open={menuOpen}
        category={hoveredCategory}
        data={jobMegaMenuData}
        onClose={() => {
          clearCloseTimer();
          setMenuOpen(false);
        }}
        onEnter={clearCloseTimer}
        secondaryHidden={hidden}
        anchorTop={anchorTop}
      />
    </Box>
  );
};

export default JobSecondaryNavBar;
